/* ==========================================================================
   Vanguarda motion — the behaviour half of component-motion.css

   Everything here is additive. If this file fails to load, the store works
   exactly as it did before: no element depends on script to become visible,
   because every hidden-by-default rule in the stylesheet is either scoped to
   html.js or applied to an attribute that only this file ever sets.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  function all(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  /* ----------------------------------------------------------------- reveal
     base.css reveals [data-reveal], but only 15 files ever set it. Rather than
     hand-edit every remaining section, tag the structural blocks here.

     The flash problem and why this does not have it: an attribute added by
     script lands after first paint, so hiding an element at that point would
     show it and then snatch it away. So anything already within the viewport
     is marked .is-instant and never animates. Only elements the shopper has
     not reached yet are ever hidden, and by definition they cannot flash.
  */
  var REVEAL_TARGETS = [
    '.section-head',
    '.collection-hero',
    '.article-hero',
    '.article-body',
    '.accordion',
    '.guarantee',
    '.empty-state',
    '.container--narrow > .rte',
    '.slider > .card',
    '.grid > .card',
    '.grid > .tile',
    '.article-card',
    '.pfeature',
    '.value-prop',
    '.feature-list__item',
    '.proof__card',
    '.quote',
    '.tiers',
    '.highlights'
  ].join(',');

  var revealObserver = null;

  function observerFor() {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;
    revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 });
    return revealObserver;
  }

  function initReveal(scope) {
    if (!document.body.classList.contains('reveal-enabled')) return;

    var nodes = all(REVEAL_TARGETS, scope).filter(function (el) {
      // never double up on the reveals the Liquid already declares
      return !el.hasAttribute('data-reveal') && !el.hasAttribute('data-reveal-auto');
    });

    var io = observerFor();
    // Where the fold sits. A little short of the true bottom, so an element
    // half in view counts as visible and is not yanked away to fade back in.
    var fold = window.innerHeight * 0.92;
    var perParent = new WeakMap();

    nodes.forEach(function (el) {
      el.setAttribute('data-reveal-auto', '');

      if (reduce || !io) {
        el.classList.add('is-instant');
        return;
      }

      var box = el.getBoundingClientRect();
      // Already on screen, or above it because we loaded mid-page.
      if (box.top < fold) {
        el.classList.add('is-instant');
        return;
      }

      // Siblings arrive in sequence rather than as a block.
      var parent = el.parentNode;
      var n = perParent.get(parent) || 0;
      perParent.set(parent, n + 1);
      el.style.setProperty('--reveal-delay', Math.min(n, 5) * 60 + 'ms');

      io.observe(el);
    });
  }

  /* ----------------------------------------------------------------- images
     Fade each photo in as it lands. Cached images report .complete straight
     away and are marked without waiting for an event that will never fire.
  */
  var IMG_TARGETS = '.card__img--main, .tile img, .article-card__media img, .rec-line__media img';

  function markLoaded() {
    this.classList.add('is-loaded');
  }

  function initImages(scope) {
    all(IMG_TARGETS, scope).forEach(function (img) {
      if (img.classList.contains('is-loaded')) return;
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('is-loaded');
        return;
      }
      img.addEventListener('load', markLoaded, { once: true });
      // A broken image must still resolve, or it leaves a permanent hole.
      img.addEventListener('error', markLoaded, { once: true });
    });
  }

  // Belt and braces: whatever happened, nothing stays invisible past 3s.
  setTimeout(function () {
    all(IMG_TARGETS).forEach(function (img) { img.classList.add('is-loaded'); });
  }, 3000);

  /* -------------------------------------------------------------- cart count
     Bump the badge whenever the number changes, so the header visibly answers
     the tap that added the item.
  */
  function initCartBump() {
    if (reduce || !('MutationObserver' in window)) return;

    all('.cart-count').forEach(function (el) {
      var last = el.textContent.trim();
      new MutationObserver(function () {
        var now = el.textContent.trim();
        if (now === last) return;
        last = now;
        el.classList.remove('is-bumped');
        void el.offsetWidth; // restart the animation
        el.classList.add('is-bumped');
        setTimeout(function () { el.classList.remove('is-bumped'); }, 500);
      }).observe(el, { childList: true, characterData: true, subtree: true });
    });
  }

  /* ------------------------------------------------------------------ header
     A hairline that appears once the page has moved, so the header stays clean
     over the hero and separated everywhere else.
  */
  function initHeader() {
    var header = document.querySelector('.header');
    if (!header) return;

    var ticking = false;
    function update() {
      header.classList.toggle('header--stuck', window.scrollY > 8);
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });
    update();
  }

  /* ----------------------------------------------------------------- drawers
     The stagger inside a drawer is gated on .drawer--anim and only while it is
     opening. The cart body re-renders on every quantity change, so without the
     gate each line would re-animate on every plus and minus press — twitchy in
     exactly the place a shopper needs to feel steady.
  */
  function initDrawers() {
    if (reduce || !('MutationObserver' in window)) return;

    all('.drawer').forEach(function (drawer) {
      var wasOpen = drawer.classList.contains('is-open');
      var timer = null;

      new MutationObserver(function () {
        var isOpen = drawer.classList.contains('is-open');
        if (isOpen === wasOpen) return;
        wasOpen = isOpen;
        if (!isOpen) {
          drawer.classList.remove('drawer--anim');
          return;
        }
        drawer.classList.add('drawer--anim');
        clearTimeout(timer);
        timer = setTimeout(function () {
          drawer.classList.remove('drawer--anim');
        }, 900);
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    });
  }

  /* --------------------------------------------------------- page transition
     Cross-fade between pages to kill the white flash. Held to 170ms: any
     longer and it stops reading as polish and starts reading as a slow site.

     Off inside the theme editor, where an intercepted navigation fights the
     editor's own routing.
  */
  function initPageTransition() {
    if (reduce || designMode) return;

    document.addEventListener('click', function (e) {
      if (e.defaultPrevented) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      var a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      if (a.hasAttribute('download')) return;
      if (a.target && a.target !== '_self') return;
      if (a.getAttribute('href').charAt(0) === '#') return;
      if (a.closest('[data-no-transition]')) return;

      var url;
      try {
        url = new URL(a.href, window.location.href);
      } catch (err) {
        return;
      }
      if (url.origin !== window.location.origin) return;
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return;
      // same page, different anchor — nothing is navigating
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;

      e.preventDefault();
      root.classList.add('is-leaving');
      setTimeout(function () { window.location.href = a.href; }, 170);
      // If the navigation is blocked or refused, do not leave the page blank.
      setTimeout(function () { root.classList.remove('is-leaving'); }, 2500);
    });

    // Coming back via the back button restores the page from cache mid-fade.
    window.addEventListener('pageshow', function () {
      root.classList.remove('is-leaving');
    });
  }

  /* ---------------------------------------------------------------- counters
     The stats band is four large numbers that sat there as dead type. Counting
     them up as they scroll into view is the one place in the store where the
     motion carries the message rather than decorating it.

     Parsed, not configured: the merchant types "40+" or "1,200" or "24h" in
     the editor and never has to know this exists. Anything without a number in
     it is left exactly as written, and the prefix and suffix are preserved so
     the plus sign and the unit never disappear mid-count.
  */
  function initCounters() {
    if (reduce || !('IntersectionObserver' in window)) return;

    var nodes = all('.stat__num').filter(function (el) {
      return !el.dataset.vgCounted;
    });
    if (!nodes.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        run(entry.target);
      });
    }, { threshold: 0.4 });

    nodes.forEach(function (el) {
      var raw = el.textContent.trim();
      var m = raw.match(/^(\D*?)([\d.,]+)(.*)$/);
      if (!m) return;

      var digits = m[2];
      // Strip thousands separators for the maths, remember them for the render.
      var grouped = digits.indexOf(',') > -1;
      var target = parseFloat(digits.replace(/,/g, ''));
      if (!isFinite(target)) return;

      // A decimal target must count in decimals or it lands on the wrong value.
      var dot = digits.replace(/,/g, '').indexOf('.');
      var places = dot > -1 ? digits.replace(/,/g, '').length - dot - 1 : 0;

      el.dataset.vgCounted = '1';
      el.dataset.vgPrefix = m[1];
      el.dataset.vgSuffix = m[3];
      el.dataset.vgTarget = String(target);
      el.dataset.vgPlaces = String(places);
      el.dataset.vgGrouped = grouped ? '1' : '';

      io.observe(el);
    });

    function render(el, value) {
      var places = Number(el.dataset.vgPlaces) || 0;
      var text = value.toFixed(places);
      if (el.dataset.vgGrouped) {
        var parts = text.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        text = parts.join('.');
      }
      el.textContent = el.dataset.vgPrefix + text + el.dataset.vgSuffix;
    }

    function run(el) {
      var target = Number(el.dataset.vgTarget);
      var duration = 1100;
      var started = null;

      // Starts fast and eases out. A linear count reads like a loading bar;
      // this reads like a number arriving at a figure.
      function ease(t) { return 1 - Math.pow(1 - t, 3); }

      function frame(now) {
        if (started === null) started = now;
        var t = Math.min(1, (now - started) / duration);
        render(el, target * ease(t));
        if (t < 1) window.requestAnimationFrame(frame);
        else render(el, target); // land on the exact figure, never 39.98
      }
      window.requestAnimationFrame(frame);
    }
  }

  /* ------------------------------------------------------------ busy buttons
     theme.js replaces the Add to bag label with a literal "…" while the fetch
     is in flight and sets aria-disabled, which base.css renders at 40% opacity.
     The most important button in the store therefore goes faint and loses its
     label at the exact moment the shopper is waiting to learn whether the tap
     worked — it reads as broken rather than busy.

     Watching the attribute rather than the click keeps this entirely out of
     theme.js: whatever sets aria-disabled, for however long, gets a spinner at
     full contrast and its width unchanged.
  */
  function initWorkingButtons() {
    if (!('MutationObserver' in window)) return;

    var mo = new MutationObserver(function (records) {
      records.forEach(function (r) {
        var el = r.target;
        if (!el.classList || !el.classList.contains('button')) return;
        var busy = el.getAttribute('aria-disabled') === 'true';
        el.classList.toggle('is-working', busy && !reduce);
        // The label is transparent while the spinner runs, so the accessible
        // name has to come from somewhere that is not the text.
        if (busy) el.setAttribute('aria-busy', 'true');
        else el.removeAttribute('aria-busy');
      });
    });

    mo.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-disabled']
    });
  }

  /* ------------------------------------------------------------------ header
     Hiding the header on the way down gives a phone back ~60px of product, and
     VgHeader in theme.js already does it. What it does not do is stay put when
     a drawer is open: scrolling inside the cart moved the page behind it on
     browsers that do not fully honour the scroll lock, and the header slid away
     underneath the overlay.
  */
  function initHeaderGuard() {
    if (!('MutationObserver' in window)) return;
    var header = document.querySelector('.header');
    if (!header) return;

    function sync() {
      var open = !!document.querySelector('.drawer.is-open');
      if (open) header.classList.remove('header--hidden');
      header.classList.toggle('header--pinned', open);
    }

    all('.drawer').forEach(function (drawer) {
      new MutationObserver(sync).observe(drawer, {
        attributes: true,
        attributeFilter: ['class']
      });
    });
    sync();
  }

  /* -------------------------------------------------------------------- boot */
  function refresh(scope) {
    initReveal(scope);
    initImages(scope);
    initCounters();
  }

  function init() {
    refresh(document);
    initCartBump();
    initHeader();
    initHeaderGuard();
    initDrawers();
    initWorkingButtons();
    initPageTransition();

    // The cart drawer replaces its own contents; new images and new
    // recommendation rows need picking up.
    if ('MutationObserver' in window) {
      all('.drawer__panel').forEach(function (panel) {
        var pending = null;
        new MutationObserver(function () {
          clearTimeout(pending);
          pending = setTimeout(function () { initImages(panel); }, 120);
        }).observe(panel, { childList: true, subtree: true });
      });
    }
  }

  // Sections re-render individually inside the theme editor.
  document.addEventListener('shopify:section:load', function (e) {
    refresh(e.target || document);
  });

  window.VGMotion = { refresh: refresh };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
